import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useCategorias } from "@/hooks/useBlog";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { ImageUploader } from "@/components/admin/ImageUploader";
import { SEOPreview } from "@/components/admin/SEOPreview";
import { PublishOptions } from "@/components/admin/PublishOptions";
import {
  ArrowLeft,
  Save,
  Eye,
  Loader2,
  Send,
} from "lucide-react";

interface ArtigoForm {
  titulo: string;
  slug: string;
  resumo: string;
  conteudo_html: string;
  imagem_destaque_url: string;
  categoria_id: string;
  seo_title: string;
  seo_description: string;
  publicado: boolean;
  destaque: boolean;
  data_publicacao: string;
  status_aprovacao: string;
}

const initialForm: ArtigoForm = {
  titulo: "",
  slug: "",
  resumo: "",
  conteudo_html: "",
  imagem_destaque_url: "",
  categoria_id: "",
  seo_title: "",
  seo_description: "",
  publicado: false,
  destaque: false,
  data_publicacao: new Date().toISOString(),
  status_aprovacao: "rascunho",
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export default function AdminArtigoEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [form, setForm] = useState<ArtigoForm>(initialForm);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const { data: categorias, isLoading: categoriasLoading } = useCategorias();

  // Fetch existing article if editing
  const { data: artigo, isLoading: artigoLoading } = useQuery({
    queryKey: ["admin-artigo", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("artigos")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: isEditing,
  });

  // Load article data into form
  useEffect(() => {
    if (artigo) {
      setForm({
        titulo: artigo.titulo || "",
        slug: artigo.slug || "",
        resumo: artigo.resumo || "",
        conteudo_html: artigo.conteudo_html || "",
        imagem_destaque_url: artigo.imagem_destaque_url || "",
        categoria_id: artigo.categoria_id || "",
        seo_title: artigo.seo_title || "",
        seo_description: artigo.seo_description || "",
        publicado: artigo.publicado || false,
        destaque: artigo.destaque || false,
        data_publicacao: artigo.data_publicacao || new Date().toISOString(),
        status_aprovacao: (artigo as any).status_aprovacao || "rascunho",
      });
      setSlugManuallyEdited(true);
    }
  }, [artigo]);

  // Auto-generate slug from title
  const handleTituloChange = useCallback(
    (value: string) => {
      setForm((prev) => ({
        ...prev,
        titulo: value,
        slug: slugManuallyEdited ? prev.slug : slugify(value),
      }));
    },
    [slugManuallyEdited]
  );

  const handleSlugChange = useCallback((value: string) => {
    setSlugManuallyEdited(true);
    setForm((prev) => ({ ...prev, slug: slugify(value) }));
  }, []);

  const updateField = useCallback(
    <K extends keyof ArtigoForm>(field: K, value: ArtigoForm[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: ArtigoForm & { sendForApproval?: boolean }) => {
      const payload = {
        titulo: data.titulo,
        slug: data.slug,
        resumo: data.resumo || null,
        conteudo_html: data.conteudo_html,
        imagem_destaque_url: data.imagem_destaque_url || null,
        categoria_id: data.categoria_id || null,
        seo_title: data.seo_title || null,
        seo_description: data.seo_description || null,
        publicado: data.publicado,
        destaque: data.destaque,
        data_publicacao: data.data_publicacao,
        status_aprovacao: data.sendForApproval ? "aguardando_aprovacao" : data.status_aprovacao,
      };

      if (isEditing) {
        const { error } = await supabase
          .from("artigos")
          .update(payload as any)
          .eq("id", id);
        if (error) throw error;
        return { id, sendForApproval: data.sendForApproval };
      } else {
        const { data: newArtigo, error } = await supabase
          .from("artigos")
          .insert(payload as any)
          .select("id")
          .single();
        if (error) throw error;
        return { id: newArtigo.id, sendForApproval: data.sendForApproval };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin-artigos"] });
      queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
      queryClient.invalidateQueries({ queryKey: ["admin-pendentes"] });
      
      if (result?.sendForApproval) {
        toast({
          title: "Enviado para aprovação",
          description: "O artigo foi enviado para revisão. Aguarde a aprovação.",
        });
        navigate(`/admin/artigos/${result.id}/preview`);
      } else {
        toast({
          title: isEditing ? "Artigo atualizado" : "Artigo criado",
          description: form.publicado
            ? "O artigo foi publicado com sucesso."
            : "O rascunho foi salvo.",
        });
        navigate("/admin/artigos");
      }
    },
    onError: (error: any) => {
      console.error("Save error:", error);
      toast({
        title: "Erro ao salvar",
        description: error.message || "Não foi possível salvar o artigo.",
        variant: "destructive",
      });
    },
  });

  const validateForm = (): boolean => {
    if (!form.titulo.trim()) {
      toast({
        title: "Título obrigatório",
        description: "Por favor, preencha o título do artigo.",
        variant: "destructive",
      });
      return false;
    }

    if (!form.slug.trim()) {
      toast({
        title: "Slug obrigatório",
        description: "Por favor, preencha o slug do artigo.",
        variant: "destructive",
      });
      return false;
    }

    if (!form.conteudo_html.trim() || form.conteudo_html === "<p></p>") {
      toast({
        title: "Conteúdo obrigatório",
        description: "Por favor, escreva o conteúdo do artigo.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSave = (publish: boolean) => {
    if (!validateForm()) return;
    saveMutation.mutate({
      ...form,
      publicado: publish,
    });
  };

  const handleSendForApproval = () => {
    if (!validateForm()) return;
    saveMutation.mutate({
      ...form,
      sendForApproval: true,
    });
  };

  const isApproved = form.status_aprovacao === "aprovado";
  const isAwaitingApproval = form.status_aprovacao === "aguardando_aprovacao";

  const isLoading = artigoLoading || categoriasLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-[400px] w-full" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {isEditing ? "Editar Artigo" : "Novo Artigo"} | Aceena Admin
        </title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/admin/artigos">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <h1 className="text-2xl font-bold text-foreground">
              {isEditing ? "Editar Artigo" : "Novo Artigo"}
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isEditing && form.slug && (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/admin/artigos/${id}/preview`}>
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </Link>
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              Salvar
            </Button>
            
            {/* Botão de enviar para aprovação - apenas se não está aprovado */}
            {!isApproved && !isAwaitingApproval && (
              <Button
                variant="secondary"
                onClick={handleSendForApproval}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-1" />
                )}
                Enviar para Aprovação
              </Button>
            )}

            {isAwaitingApproval && (
              <span className="text-sm text-amber-600 font-medium px-3 py-2 bg-amber-50 rounded-md">
                Aguardando aprovação
              </span>
            )}
            
            {/* Botão de publicar - apenas se aprovado */}
            {isApproved && (
              <Button
                onClick={() => handleSave(true)}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : null}
                {form.publicado ? "Atualizar" : "Publicar"}
              </Button>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left panel - Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Título */}
            <div className="space-y-2">
              <Label htmlFor="titulo">Título *</Label>
              <Input
                id="titulo"
                placeholder="Digite o título do artigo"
                value={form.titulo}
                onChange={(e) => handleTituloChange(e.target.value)}
                className="text-lg font-medium"
              />
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <Label htmlFor="slug">Slug (URL) *</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">/blog/</span>
                <Input
                  id="slug"
                  placeholder="url-do-artigo"
                  value={form.slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                />
              </div>
            </div>

            {/* Resumo */}
            <div className="space-y-2">
              <Label htmlFor="resumo">Resumo</Label>
              <Textarea
                id="resumo"
                placeholder="Breve descrição do artigo para listagens e cards"
                value={form.resumo}
                onChange={(e) => updateField("resumo", e.target.value)}
                rows={3}
              />
            </div>

            {/* Editor */}
            <div className="space-y-2">
              <Label>Conteúdo *</Label>
              <RichTextEditor
                content={form.conteudo_html}
                onChange={(content) => updateField("conteudo_html", content)}
              />
            </div>
          </div>

          {/* Right panel - Settings */}
          <div className="space-y-6">
            {/* Imagem de destaque */}
            <div className="bg-card rounded-lg border border-border p-4 space-y-4">
              <Label>Imagem de Destaque</Label>
              <ImageUploader
                value={form.imagem_destaque_url}
                onChange={(url) => updateField("imagem_destaque_url", url)}
              />
            </div>

            {/* Opções de Publicação */}
            <div className="bg-card rounded-lg border border-border p-4">
              <PublishOptions
                publicado={form.publicado}
                onPublicadoChange={(v) => updateField("publicado", v)}
                destaque={form.destaque}
                onDestaqueChange={(v) => updateField("destaque", v)}
                dataPublicacao={form.data_publicacao}
                onDataPublicacaoChange={(v) => updateField("data_publicacao", v)}
                categoriaId={form.categoria_id}
                onCategoriaChange={(v) => updateField("categoria_id", v)}
                categorias={categorias || []}
              />
            </div>

            {/* SEO */}
            <div className="bg-card rounded-lg border border-border p-4 space-y-4">
              <h3 className="font-medium text-foreground">SEO</h3>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="seo-title">Título SEO</Label>
                  <span
                    className={`text-xs ${
                      form.seo_title.length > 60
                        ? "text-destructive"
                        : "text-muted-foreground"
                    }`}
                  >
                    {form.seo_title.length}/60
                  </span>
                </div>
                <Input
                  id="seo-title"
                  placeholder={form.titulo || "Título para mecanismos de busca"}
                  value={form.seo_title}
                  onChange={(e) => updateField("seo_title", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="seo-description">Meta Description</Label>
                  <span
                    className={`text-xs ${
                      form.seo_description.length > 160
                        ? "text-destructive"
                        : "text-muted-foreground"
                    }`}
                  >
                    {form.seo_description.length}/160
                  </span>
                </div>
                <Textarea
                  id="seo-description"
                  placeholder={form.resumo || "Descrição para mecanismos de busca"}
                  value={form.seo_description}
                  onChange={(e) =>
                    updateField("seo_description", e.target.value)
                  }
                  rows={3}
                />
              </div>

              <SEOPreview
                title={form.seo_title || form.titulo}
                description={form.seo_description || form.resumo}
                slug={form.slug}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
